(function initializeFamilyNavigation(root, createApi) {
  'use strict';
  const api = createApi();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root?.document || root.AlvauraFamilyNavigation) return;
  root.AlvauraFamilyNavigation = api;

  const parse = (value) => {
    try { return JSON.parse(value); } catch { return null; }
  };
  const readSource = (selector) => {
    const source = parse(selector.querySelector('[data-alvaura-family-source]')?.textContent);
    const productInfo = selector.closest('product-info');
    const form = Array.from(productInfo?.querySelectorAll('form') || []).find(
      (item) => item.getAttribute('id') === `product-form-${selector.dataset.sectionId}`
    );
    const variantInput = form?.querySelector('input[name="id"].product-variant-id');
    const inputVariantId = api.normalize(variantInput?.value);
    const sizeGroup = Array.from(productInfo?.querySelectorAll('variant-selects [data-alvaura-option-name]') || [])
      .find((group) => api.normalize(group.dataset.alvauraOptionName).toLowerCase() === 'size');
    const selectedSize = sizeGroup?.querySelector('select')?.value
      || sizeGroup?.querySelector('input:checked')?.value || '';
    const matchingAvailability = Array.from(productInfo?.querySelectorAll('alvaura-availability') || [])
      .filter((element) => String(element.dataset.sectionId) === String(selector.dataset.sectionId));
    const availability = matchingAvailability.length === 1 ? matchingAvailability[0] : null;
    const busy = availability?.getAttribute('aria-busy') === 'true';
    const synced = availability?.dataset.formSync === 'ready' && !busy;
    const syncedVariantId = synced ? api.normalize(availability.dataset.formVariantId) : '';
    const usesSyncedId = Boolean(variantInput && !inputVariantId && syncedVariantId);
    const variantId = inputVariantId || (usesSyncedId ? syncedVariantId : '');
    const conflict = Boolean(inputVariantId && syncedVariantId && inputVariantId !== syncedVariantId);
    const pending = busy || conflict || matchingAvailability.length > 1 || (usesSyncedId && !selectedSize);
    return { source, variantId, selectedSize, pending };
  };
  const updateLink = (link, report = false) => {
    const selector = link.closest('[data-alvaura-family-selector]');
    if (!selector) return false;
    const { source, variantId, selectedSize, pending } = readSource(selector);
    const target = {
      options: parse(link.dataset.familyTargetOptions),
      variants: parse(link.dataset.familyTargetVariants),
      compositionMetal: link.dataset.familyTargetMetal,
    };
    const selection = api.selectTargetVariant({ source, variantId, selectedSize, pending, target });
    const url = selection.ok && api.targetUrl(link.dataset.familyTargetUrl, root.location.href, selection.variantId);
    if (url) {
      link.href = url;
      link.dataset.productUrl = url;
      link.removeAttribute('aria-disabled');
    } else {
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
    }
    const message = selector.querySelector('[data-alvaura-family-navigation-message]');
    if (message && (report || url)) {
      message.hidden = Boolean(url);
      message.textContent = url ? '' : [
        selection.size && selector.dataset.familyUnavailableLabel?.replace('__SIZE__', selection.size),
        link.dataset.familyChooseLabel || selector.dataset.familyChooseLabel,
      ].filter(Boolean).join('. ');
    }
    return Boolean(url);
  };
  const refresh = () => root.document.querySelectorAll('[data-alvaura-family-link]').forEach((link) => updateLink(link));
  const onIntent = (event) => {
    const link = event.target.closest?.('[data-alvaura-family-link]');
    if (!link) return;
    const activates = event.type === 'click' || event.type === 'auxclick'
      || (event.type === 'keydown' && event.key === 'Enter');
    if (event.type === 'keydown' && !activates) return;
    if (!updateLink(link, activates) && activates) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  for (const eventName of ['pointerdown', 'focusin', 'click', 'auxclick', 'keydown', 'contextmenu']) {
    root.document.addEventListener(eventName, onIntent, true);
  }
  root.document.addEventListener('change', () => root.requestAnimationFrame(refresh), true);
  root.document.addEventListener('shopify:section:load', refresh);
  if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})(typeof window === 'object' ? window : null, function createFamilyNavigationApi() {
  'use strict';
  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');
  const sizeIndex = (product) => Array.isArray(product?.options)
    ? product.options.findIndex((name) => normalize(name).toLowerCase() === 'size') : -1;

  function selectTargetVariant({ source, variantId, selectedSize = '', pending = false, target }) {
    if (!Array.isArray(source?.variants) || !Array.isArray(target?.variants)
      || !Array.isArray(source?.options) || !Array.isArray(target?.options)) return { ok: false, reason: 'invalid-data' };
    const current = source.variants.filter((variant) => String(variant.id) === String(variantId));
    if (current.length !== 1 || pending) return { ok: false, reason: 'unresolved-source' };
    const sourceIndex = sizeIndex(source);
    if (sourceIndex < 0) return { ok: true, variantId: null };
    const size = normalize(current[0].options?.[sourceIndex]);
    if (!size || (selectedSize && normalize(selectedSize) !== size)) return { ok: false, reason: 'pending-size', size: normalize(selectedSize) };
    const targetIndex = sizeIndex(target);
    if (targetIndex < 0) return { ok: false, reason: 'missing-size-axis', size };
    let candidates = target.variants.filter((variant) => normalize(variant.options?.[targetIndex]) === size);
    const metalIndex = target.options.findIndex((name) => normalize(name).toLowerCase() === 'metal');
    if (metalIndex >= 0 && target.compositionMetal) {
      candidates = candidates.filter((variant) => variant.options?.[metalIndex] === target.compositionMetal);
    }
    if (candidates.length !== 1 || !/^\d+$/.test(String(candidates[0].id))) {
      return { ok: false, reason: candidates.length ? 'ambiguous-target' : 'missing-size', size };
    }
    return { ok: true, variantId: String(candidates[0].id), size };
  }

  function targetUrl(target, current, variantId) {
    try {
      if (typeof target !== 'string' || !target.trim()) return null;
      const source = new URL(current);
      const destination = new URL(target, source);
      if (destination.origin !== source.origin || !/\/products\//.test(destination.pathname)) return null;
      for (const key of ['view', 'preview_theme_id', '_ab', '_fd', '_sc']) {
        if (source.searchParams.has(key)) destination.searchParams.set(key, source.searchParams.get(key));
      }
      destination.searchParams.delete('variant');
      destination.searchParams.delete('option_values');
      if (variantId != null) {
        if (!/^\d+$/.test(String(variantId))) return null;
        destination.searchParams.set('variant', String(variantId));
      }
      return destination.href;
    } catch { return null; }
  }
  return { normalize, selectTargetVariant, targetUrl };
});
